import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Music2, Gamepad2, Palette, Mic2, Store, Star, Sparkles } from 'lucide-react';
import { Nav, Footer } from './LandingPage';

const DISPLAY = 'font-display font-extrabold tracking-tighter';
const BODY = 'font-body';
const METALLIC = 'text-transparent bg-clip-text bg-gradient-to-br from-gray-900 via-gray-600 to-gray-900';
const METALLIC_DARKBG = 'text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/40';

const EXAMPLES = [
  { username: 'influencer1', niche: 'Musician', Icon: Music2, color: '#1DB954', desc: 'Inline Spotify players, tour dates, merch and a tip jar for fan support.', seo: '/link-in-bio-for-musicians' },
  { username: 'influencer2', niche: 'Streamer', Icon: Gamepad2, color: '#9146FF', desc: 'Twitch, YouTube and Discord front and centre, with clip embeds.', seo: '/link-in-bio-for-streamers' },
  { username: 'influencer3', niche: 'Artist', Icon: Palette, color: '#EA4C89', desc: 'Portfolio links, print shop, commissions — styled to match the work.', seo: '/link-in-bio-for-artists' },
  { username: 'influencer4', niche: 'Podcaster', Icon: Mic2, color: '#FA243C', desc: 'Latest episode embedded, every listening platform one tap away.', seo: '/link-in-bio-for-podcasters' },
  { username: 'influencer5', niche: 'Small business', Icon: Store, color: '#10B981', desc: 'Menu, ordering, WhatsApp and socials on one clean mobile page.', seo: '/link-in-bio-for-small-business' },
  { username: 'demouser', niche: 'Creator demo', Icon: Star, color: '#EAB308', desc: 'The full demo profile — embeds, fan page, widgets, the lot.', seo: null },
  { username: 'linkmonkey', niche: 'All 63 icons', Icon: Sparkles, color: '#0055FF', desc: 'Every brand icon LinkMonkey supports, on one showcase page.', seo: null },
];

export default function Examples() {
  return (
    <div className="min-h-screen bg-[#D9E9FC] text-gray-950 antialiased selection:bg-gray-950 selection:text-white" data-testid="examples-page">
      <Helmet>
        <title>Link in Bio Examples — LinkMonkey Pages by Niche</title>
        <meta name="description" content="See live LinkMonkey link-in-bio examples for musicians, streamers, artists, podcasters and small businesses. Real pages you can click through." />
        <link rel="canonical" href="https://linkmonkey.io/examples" />
        <link rel="preconnect" href="https://api.fontshare.com" crossOrigin="anonymous" />
        <link href="https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@400,500,700,800,900,400i&f[]=satoshi@400,500,700&display=swap" rel="stylesheet" />
      </Helmet>

      <Nav />

      <section className="relative pt-36 md:pt-44 pb-16 px-6 md:px-10 bg-[#D9E9FC] overflow-hidden">
        <div className="absolute inset-0 -z-10 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(0,85,255,0.08) 0%, transparent 65%)' }} />
        <div className="max-w-4xl mx-auto text-center">
          <p className={`${BODY} text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-5`}>Examples</p>
          <h1 className={`${DISPLAY} text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.95] mb-6`}>
            <span className={`block ${METALLIC}`}>Real pages.</span>
            <span className="block italic font-light tracking-tight text-[#0055FF]">Click through.</span>
          </h1>
          <p className={`${BODY} text-gray-600 text-base md:text-lg max-w-xl mx-auto`}>
            Live LinkMonkey pages by niche — see what yours could look like, then build it free.
          </p>
        </div>
      </section>

      <section className="relative pb-24 md:pb-32 px-6 md:px-10 bg-[#D9E9FC]">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {EXAMPLES.map(({ username, niche, Icon, color, desc, seo }) => (
            <div key={username} className="group relative rounded-2xl border border-gray-200 bg-white shadow-sm p-7 hover:border-gray-300 hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.06)] transition-[border-color,box-shadow] flex flex-col" data-testid={`example-card-${username}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                style={{ background: `${color}14`, boxShadow: `0 0 0 1px ${color}33` }}>
                <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.75} />
              </div>
              <h2 className={`${DISPLAY} text-gray-900 text-2xl mb-2 tracking-tight`}>{niche}</h2>
              <p className={`${BODY} text-gray-600 text-sm leading-relaxed mb-6 flex-1`}>{desc}</p>
              <div className="flex items-center justify-between gap-3">
                <Link to={`/${username}`} className={`${BODY} inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:text-[#0055FF] transition-colors`} data-testid={`example-view-${username}`}>
                  View live page <ArrowUpRight className="w-4 h-4" />
                </Link>
                {seo && (
                  <Link to={seo} className={`${BODY} text-xs text-gray-400 hover:text-gray-700 transition-colors`}>
                    Why it works →
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Dark accent CTA */}
      <section className="relative py-24 px-6 md:px-10 bg-[#06070C] text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 60% 55% at 50% 100%, rgba(0,85,255,0.18) 0%, transparent 65%)' }} />
        <div className="relative">
          <h2 className={`${DISPLAY} text-[clamp(2rem,5vw,4rem)] leading-[0.95] mb-5`}>
            <span className={METALLIC_DARKBG}>Yours could be next.</span>
          </h2>
          <p className={`${BODY} text-white/60 mb-9`}>Free to start. Live in two minutes.</p>
          <Link to="/signup">
            <button className={`${BODY} inline-flex items-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-7 py-4 rounded-full hover:bg-[#3377FF] transition-colors shadow-[0_4px_14px_rgba(0,85,255,0.3)] hover:scale-[1.02]`}
              data-testid="examples-cta">
              Create your free page
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
